/**
 * Dashboard section visibility: keys and labels shared with the server (users.dashboard_sections JSON).
 */

export type DashboardSectionId =
  | 'pending'
  | 'tasks'
  | 'leads'
  | 'sales'
  | 'inventory'
  | 'robots'
  | 'activity';

export const DASHBOARD_SECTION_ORDER: DashboardSectionId[] = [
  'pending',
  'tasks',
  'leads',
  'sales',
  'inventory',
  'robots',
  'activity',
];

/** Cards that participate in the flowing grid (not full-width Activity log). Order = fill rows left-to-right, up to 3 per row. */
export const DASHBOARD_FLOW_CARD_IDS: Exclude<DashboardSectionId, 'activity'>[] = [
  'pending',
  'tasks',
  'leads',
  'sales',
  'inventory',
  'robots',
];

export const DASHBOARD_SECTION_LABELS: Record<DashboardSectionId, string> = {
  pending: 'Orders',
  tasks: 'Your tasks',
  leads: 'Leads',
  sales: 'Sales chart',
  inventory: 'Low stock inventory',
  robots: 'Robots online',
  activity: 'Activity log',
};

const STORAGE_KEY_LEGACY = 'techforce_dashboard_sections_v1';

const ALL_TRUE: Record<DashboardSectionId, boolean> = {
  pending: true,
  tasks: true,
  leads: true,
  sales: true,
  inventory: true,
  robots: true,
  activity: true,
};

/** Merge API or legacy payload with defaults (missing keys default to visible). */
export function normalizeDashboardSections(raw: unknown | null | undefined): Record<DashboardSectionId, boolean> {
  const out = { ...ALL_TRUE };
  if (!raw || typeof raw !== 'object') return out;
  for (const id of DASHBOARD_SECTION_ORDER) {
    const v = (raw as Record<string, unknown>)[id];
    if (typeof v === 'boolean') out[id] = v;
  }
  return out;
}

export function defaultDashboardSections(): Record<DashboardSectionId, boolean> {
  return { ...ALL_TRUE };
}

/** One-time read of pre-sync localStorage layout (same device) if the API is unavailable. */
export function loadDashboardSectionsLegacy(userId: number): Record<DashboardSectionId, boolean> | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LEGACY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const userRow = parsed[String(userId)];
    if (!userRow || typeof userRow !== 'object') return null;
    return normalizeDashboardSections(userRow);
  } catch {
    return null;
  }
}

export function clearDashboardSectionsLegacy(userId: number): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LEGACY);
    if (!raw) return;
    const all = JSON.parse(raw) as Record<string, unknown>;
    delete all[String(userId)];
    if (Object.keys(all).length === 0) {
      localStorage.removeItem(STORAGE_KEY_LEGACY);
    } else {
      localStorage.setItem(STORAGE_KEY_LEGACY, JSON.stringify(all));
    }
  } catch {
    /* ignore */
  }
}

export function countVisibleSections(prefs: Record<DashboardSectionId, boolean>): number {
  return DASHBOARD_SECTION_ORDER.filter((id) => prefs[id]).length;
}
